import type { PartnerClient } from '@epilot/sdk/identity'
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'

import { getIdentityClient } from '@/api/identity-client'

/** Pages of 100 read for a picker before it stops (2,000 applications). */
const MAX_CLIENT_PAGES = 20

export const useOidcClients = () =>
  useInfiniteQuery({
    queryKey: ['identity', 'clients'],
    queryFn: async ({ pageParam }) =>
      (await getIdentityClient().listClients({ size: 100, cursor: pageParam }))
        .data,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_cursor || undefined,
    retry: false,
    refetchOnWindowFocus: false
  })

/** Every Epilot IdP application of this stage, whatever its partner or status, for pickers. */
export const useAllOidcClients = () =>
  useQuery({
    queryKey: ['identity', 'clients', 'all'],
    queryFn: async () => {
      const clients: PartnerClient[] = []
      let cursor: string | undefined

      for (let page = 0; page < MAX_CLIENT_PAGES; page += 1) {
        const { data } = await getIdentityClient().listClients({
          size: 100,
          cursor
        })

        clients.push(...data.results)
        cursor = data.next_cursor || undefined
        if (!cursor) break
      }

      return clients
    },
    retry: false,
    refetchOnWindowFocus: false
  })

export const useOidcClient = (id: string) =>
  useQuery({
    queryKey: ['identity', 'client', id],
    queryFn: async () =>
      (await getIdentityClient().getOidcClient({ client_id: id })).data,
    enabled: Boolean(id),
    retry: false,
    refetchOnWindowFocus: false
  })

export const useOidcOrganizations = (id: string) =>
  useQuery({
    queryKey: ['identity', 'organizations', id],
    // Explicit form: the generated type of this operation omits its path-level client_id.
    queryFn: async () =>
      (
        await getIdentityClient().listClientOrganizations([
          { name: 'client_id', value: id, in: 'path' }
        ])
      ).data.results,
    enabled: Boolean(id),
    retry: false,
    refetchOnWindowFocus: false
  })

export const useRefreshIdentity = () => {
  const queryClient = useQueryClient()

  return () => queryClient.invalidateQueries({ queryKey: ['identity'] })
}
